/**
 * URL-shape rules for the site's three languages.
 *
 * French is served UNPREFIXED at the root (`/about`) and is also the
 * `x-default`; the other two languages live behind a locale segment
 * (`/en/about`, `/es/about`). The locale in the URL is authoritative — it
 * decides the language of the page, ahead of any persisted preference or
 * `navigator.language` — which is the whole point of having distinct URLs:
 * a crawler must be able to fetch one URL and get one, stable language.
 *
 * Everything here is pure string work on ROOT-RELATIVE paths, deliberately
 * decoupled from the router and from `window`, so the same rules drive the
 * runtime history wrapper, the canonical/hreflang tags, the sitemap and
 * `robots.txt`.
 */
import { SUPPORTED_LANG_CODES, type SupportedLangCode } from './language'

/** The language served without a URL prefix. Also the sitemap's `x-default`. */
export const DEFAULT_LOCALE: SupportedLangCode = 'fr'

/** Languages that DO carry a `/xx` URL segment. */
export const PREFIXED_LOCALES: SupportedLangCode[] = SUPPORTED_LANG_CODES.filter(
  (code) => code !== DEFAULT_LOCALE,
)

/**
 * Path prefixes that are never localized in the URL.
 *
 * The admin back-office is not search surface — it is `Disallow`ed in
 * `robots.txt` and sits behind auth — so multiplying it across three locale
 * prefixes would only widen the crawlable surface for zero SEO gain.
 */
export const UNLOCALIZED_PATH_PREFIXES: string[] = ['/manage']

export interface ParsedLocalePath {
  /** Language the URL asks for; {@link DEFAULT_LOCALE} when unprefixed. */
  locale: SupportedLangCode
  /** The path with the locale segment removed. Always starts with `/`. */
  path: string
  /** Whether the URL actually carried a supported locale segment. */
  hasLocalePrefix: boolean
}

function normalizePath(pathname: string): string {
  if (!pathname) return '/'
  return pathname.startsWith('/') ? pathname : `/${pathname}`
}

function isPrefixedLocale(segment: string): segment is SupportedLangCode {
  return PREFIXED_LOCALES.some((locale) => locale === segment)
}

/** True for the admin surface, which keeps its path whatever the language. */
export function isLocalizablePath(pathname: string): boolean {
  const path = normalizePath(pathname)
  return !UNLOCALIZED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

/**
 * Splits a browser path into the language it asks for and the language-free
 * path the router should match.
 *
 * `/en/about` -> `{ locale: 'en', path: '/about' }`
 * `/en`, `/en/` -> `{ locale: 'en', path: '/' }`
 * `/about` -> `{ locale: 'fr', path: '/about' }` (French is the root)
 */
export function parseLocalePath(pathname: string): ParsedLocalePath {
  const path = normalizePath(pathname)
  const [, firstSegment = '', ...rest] = path.split('/')

  if (!isPrefixedLocale(firstSegment)) {
    return { locale: DEFAULT_LOCALE, path, hasLocalePrefix: false }
  }

  const remainder = rest.join('/')
  return {
    locale: firstSegment,
    path: remainder ? `/${remainder}` : '/',
    hasLocalePrefix: true,
  }
}

/**
 * The inverse of {@link parseLocalePath}: puts a language-free path back on
 * the URL shape a given language is served at.
 *
 * The site root keeps its trailing slash in every language (`/`, `/en/`,
 * `/es/`) so a locale's home page canonicalises to exactly one URL, and that
 * URL is byte-identical to its sitemap `<loc>`.
 */
export function localizedPath(path: string, locale: SupportedLangCode): string {
  const normalized = normalizePath(path)
  if (locale === DEFAULT_LOCALE || !isLocalizablePath(normalized)) return normalized
  return normalized === '/' ? `/${locale}/` : `/${locale}${normalized}`
}

/**
 * Applies {@link localizedPath} to a full href, leaving search and hash alone.
 *
 * The router hands the history layer `/investigations?page=2#facts`; only the
 * path part carries the locale.
 */
export function localizedHref(href: string, locale: SupportedLangCode): string {
  const boundary = href.search(/[?#]/)
  if (boundary === -1) return localizedPath(href, locale)
  return `${localizedPath(href.slice(0, boundary), locale)}${href.slice(boundary)}`
}

/**
 * True for `/fr` and `/fr/...` — the one wrong guess worth handling.
 *
 * French is at the root, so `/fr/about` is not a URL this app ever emits. It
 * is however the single most predictable thing a person or a crawler will
 * try once they have seen `/en/about`, so it is redirected to the real French
 * URL rather than 404'd (a permanent redirect at the edge, see `vercel.json`,
 * plus the client-side fallback in `redirectedDefaultLocalePath`).
 *
 * Any OTHER unknown prefix (`/de/about`, `/xx`) is deliberately left to 404:
 * those URLs were never published, are not in the sitemap, and mapping an
 * open-ended set of prefixes onto real pages would mint unbounded duplicate
 * URLs for crawlers to chew through.
 */
export function isDefaultLocaleAliasPath(pathname: string): boolean {
  const path = normalizePath(pathname)
  return path === `/${DEFAULT_LOCALE}` || path.startsWith(`/${DEFAULT_LOCALE}/`)
}

/**
 * The canonical path a `/fr...` alias should be sent to, or `null` when the
 * path is not an alias and must be left exactly as it is.
 */
export function redirectedDefaultLocalePath(pathname: string): string | null {
  if (!isDefaultLocaleAliasPath(pathname)) return null
  const remainder = normalizePath(pathname).slice(`/${DEFAULT_LOCALE}`.length)
  return remainder || '/'
}
